import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { checkBookingGaps } from "@/components/tpr/BookingFlows/RepresentativeBookingFlow/bookingValidation.utils";
import { isMemberAnAdmin, isMemberAPracticeManager } from "@/core/auth";
import { useMember } from "@/contexts/memberContext";
import { occursOnDate } from "@/lib/recurrence";
import { BookingService } from "@/services/BookingService";
import { CommentService } from "@/services/CommentsService";
import { OrganisationService } from "@/services/OrganisationService";
import { countPastOccurrences } from "@/utils/countPastOccurrences";
import { isPastDateBooking } from "@/utils/isPastDateBooking";
import { calculateEndTime, createLocationSlug } from "./utils";
import type { BookingGap } from "@/components/tpr/BookingFlows/RepresentativeBookingFlow/bookingValidation.utils";
import type { BookingInterceptionResult } from "@/interceptors/booking";
import type { Booking } from "@/types/Booking";
import type { CanBookValidationResponse, RecurringConflictDate } from "@/types/CanBookValidation";
import type { CreateBookingParams } from "@/api/bookingApi";
import type {
  BookingType,
  FormData,
  MakeABookingDialogProviderProps,
  MakeABookingDialogState,
  ScreenId,
} from "./types";
import { DEFAULT_DURATION_MINUTES } from "./types";
import { utcDayKey } from "@/lib/calendarDay";
import { endTimeToMinutes } from "@/utils/bookingTime";

const BUFFER_MINUTES = 15;

/**
 * UTC calendar day → YYYY-MM-DD for the booking API. The cell date is UTC-midnight of the centre's
 * day; deriving the day with LOCAL getters sent the previous day to /can-book for viewers west of
 * UTC (validating against a day they couldn't see). utcDayKey keeps it viewer-timezone independent.
 */
const formatIsoDate = (d: Date): string => utcDayKey(d);

/**
 * Convert time string (HHMM) to minutes since midnight
 */
const timeToMinutes = (time: string): number => {
  const hours = parseInt(time.substring(0, 2), 10);
  const minutes = parseInt(time.substring(2, 4), 10);
  return hours * 60 + minutes;
};

/**
 * Calculate the time range including buffers
 */
const getTimeRangeWithBuffers = (
  startTime: string,
  endTime: string,
  hasBuffers: boolean,
): { start: number; end: number; bufferStart: number; bufferEnd: number } => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = endTimeToMinutes(endTime, startTime);

  if (!hasBuffers) {
    return {
      start: startMinutes,
      end: endMinutes,
      bufferStart: startMinutes,
      bufferEnd: endMinutes,
    };
  }

  return {
    start: startMinutes,
    end: endMinutes,
    bufferStart: Math.max(0, startMinutes - BUFFER_MINUTES),
    bufferEnd: Math.min(1440, endMinutes + BUFFER_MINUTES),
  };
};

/** Pick the most relevant blocking reason from a /can-book response (only when canBook is false). */
export const extractCanBookMessage = (r: CanBookValidationResponse): string | null => {
  if (r.canBook) return null;
  const v = r.validations;
  const ordered = [
    v.inputValidation,
    v.memberPermissions,
    v.locationPermission,
    v.slotBookingAuthorization,
    v.slotHoursAvailable,
    v.roomAvailability,
    v.amenityAvailability,
    v.recurringConflicts,
    v.entityExistence,
    v.crossRoomConflict,
  ];
  const firstInvalid = ordered.find((x) => x && x.valid === false);
  return firstInvalid?.message ?? "This time isn't available — please pick another slot.";
};

export const MakeABookingDialogContext = createContext<MakeABookingDialogState | null>(null);

const createInitialFormData = (
  bookingResult: BookingInterceptionResult | null,
  currentUser: { id: string; firstName: string; lastName: string } | null,
): FormData => {
  const details = bookingResult?.details;

  return {
    date: details?.date || new Date(),
    time: details?.time || "0900",
    roomId: details?.roomId || "",
    roomName: details?.roomName || "",
    locationId: details?.locationId || "",
    locationSlug: details?.location?.slug || createLocationSlug(details?.location?.name || ""),
    locationName: details?.location?.name || "",
    memberId: currentUser?.id || "",
    memberName: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : "",
    memberLocked: bookingResult?.memberLocked || false,
    durationMinutes: DEFAULT_DURATION_MINUTES,
    isRecurring: false,
    isGroupBooking: false,
    bookingType: "adhoc" as BookingType,
    recurrencePattern: null,
    waiveBilling: false,
    staffNote: "",
  };
};

export const MakeABookingDialogProvider = ({
  bookingResult,
  onClose,
  onBookingCreated,
  children,
}: MakeABookingDialogProviderProps) => {
  const { member: currentUser } = useMember();

  // Navigation state
  const [currentScreen, setCurrentScreen] = useState<ScreenId>("book");
  const [screenHistory, setScreenHistory] = useState<ScreenId[]>(["book"]);

  // Form data
  const [formData, setFormData] = useState<FormData>(() =>
    createInitialFormData(bookingResult, currentUser),
  );

  // API state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdBooking, setCreatedBooking] = useState<Booking | null>(null);

  // Task 10 — elevated cross-member buffer override. Set when the REAL create attempt 409s with
  // conflictType "buffer_overlap" for an elevated caller (see submitBooking's catch block below).
  const [bufferOverrideOpen, setBufferOverrideOpen] = useState(false);
  const [bufferOverrideMessage, setBufferOverrideMessage] = useState<string | null>(null);

  // Availability state
  const [roomBookings, setRoomBookings] = useState<Booking[]>([]);

  // R11 org extension (Phase 1, warning-only): the set of member IDs in the current member's
  // organisation. Lets the gap check treat two DIFFERENT org members' bookings either side of a
  // small gap as continuous. undefined when the current member has no organisation → checkBookingGaps
  // behaves exactly as before. Billing is NOT affected in Phase 1.
  const [sameOrgMemberIds, setSameOrgMemberIds] = useState<Set<string> | undefined>(undefined);

  useEffect(() => {
    const orgId =
      currentUser?.ownerOfOrganisationId || currentUser?.organisation?.id || currentUser?.organisationId;
    if (!orgId) {
      setSameOrgMemberIds(undefined);
      return;
    }
    let cancelled = false;
    new OrganisationService()
      .getOrganisationById(orgId)
      .then((org) => {
        if (cancelled) return;
        setSameOrgMemberIds(
          org
            ? new Set<string>([...(org.members ?? []), org.ownerId].filter(Boolean) as string[])
            : undefined,
        );
      })
      .catch(() => {
        if (!cancelled) setSameOrgMemberIds(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, currentUser?.organisation?.id, currentUser?.organisationId, currentUser?.ownerOfOrganisationId]);

  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [isTimeSlotAvailable, setIsTimeSlotAvailable] = useState(true);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const bookingsLoadedRef = useRef(false);
  // Identity of the booking dialog we last initialised the form for. The init effect below keys off
  // this instead of the raw `bookingResult`/`currentUser` object references so a mid-dialog re-render
  // (the calendar handing down a fresh-but-identical bookingResult, or the member context refetching)
  // does NOT reset the form and silently clobber an elevated user's on-behalf member selection — which
  // dropped the gap/continuous warnings for admins booking for someone else.
  const initialisedForKeyRef = useRef<string | null>(null);

  // Server /can-book pre-flight. The server is the authority for the submit gate: it can BLOCK even
  // when the fail-fast local check passed (e.g. recurring-occurrence clash, slot hours, permissions).
  // `serverCanBook === null` means "not yet known / call errored" → we don't block on it (the POST
  // /bookings handler is the ultimate backstop). See hearth/docs/BOOKING_RULES.md (R7/R12).
  const [serverCanBook, setServerCanBook] = useState<boolean | null>(null);
  const [serverValidationMessage, setServerValidationMessage] = useState<string | null>(null);
  // Non-blocking warnings from /can-book (e.g. the termination-waitlist notice when booking for a
  // member whose membership is ending). Surfaced so an elevated user booking on behalf sees the
  // warning but is still allowed to proceed.
  const [serverWarnings, setServerWarnings] = useState<string[]>([]);
  const [isCheckingCanBook, setIsCheckingCanBook] = useState(false);
  // Per-occurrence conflicts for a recurring booking, surfaced as a clickable list in BookScreen.
  const [recurringConflictDates, setRecurringConflictDates] = useState<RecurringConflictDate[]>([]);
  const canBookSeqRef = useRef(0);

  // Gap warnings (see BookingGap: continuous-booking for same-member, unbookable-gap otherwise)
  const [bookingGaps, setBookingGaps] = useState<BookingGap[]>([]);

  // R22 — multi-room same-time: booking a second room at an overlapping time is allowed, but the
  // member must be warned (they pay for both). Holds the member's own overlapping bookings in
  // OTHER rooms on this date. Non-blocking.
  const [multiRoomConflicts, setMultiRoomConflicts] = useState<{ startTime: string; endTime: string }[]>([]);

  // Compute elevated user status
  const isElevatedUser = useMemo(() => {
    return isMemberAnAdmin(currentUser) || isMemberAPracticeManager(currentUser);
  }, [currentUser]);

  // A booking note is mandatory when an elevated user books for THEMSELVES. Staff self-bookings are
  // routinely waived or discounted by finance under arrangements not visible in the system, so the
  // booker must record why. Booking on behalf of ANOTHER member is optional (the member is the
  // billable party and the booking speaks for itself). Mirrors the RepresentativeBookingFlow gate.
  const requiresStaffNote = useMemo(
    () => isElevatedUser && !!currentUser && (!formData.memberId || formData.memberId === currentUser.id),
    [isElevatedUser, currentUser, formData.memberId],
  );

  // R1/R2a/R7 — honour the interceptor verdict. A "declined" result (past date, beyond the
  // role-aware future cap, hard overlap, shuttered room) must block the booking form entirely.
  // Historically this dialog ignored bookingResult.status, which let members through to book in
  // the past / over a clash. We now surface the decline and refuse to submit.
  const isDeclined = bookingResult?.status === "declined";
  const declineMessage = isDeclined ? bookingResult?.message ?? "This booking can't be made." : null;
  const declineReason =
    isDeclined && bookingResult?.data && typeof bookingResult.data === "object" && "reason" in bookingResult.data
      ? String((bookingResult.data as { reason?: unknown }).reason ?? "")
      : null;

  // R2b — elevated user booking a past date within the billing window.
  // Delegates to the shared isPastDateBooking helper (UTC-midnight comparison; no warning-copy coupling).
  const isPastDateWarning = useMemo(
    () => isPastDateBooking(isElevatedUser, formData.date),
    [isElevatedUser, formData.date],
  );

  // Count of past occurrences for the banner copy ("N past occurrence(s) will be created").
  const pastOccurrenceCount = useMemo(() => {
    if (!isPastDateWarning || !formData.date) return 0;
    const recurrence = formData.recurrencePattern
      ? { frequency: formData.recurrencePattern.frequency, interval: formData.recurrencePattern.interval }
      : null;
    return countPastOccurrences(formData.date, recurrence);
  }, [isPastDateWarning, formData.date, formData.recurrencePattern]);

  // Initialize form data when a NEW booking dialog is opened. We deliberately key off the slot's
  // stable identity (date/time/room/location + the logged-in user's id STRING), not the
  // bookingResult/currentUser object references: those churn on ordinary parent re-renders and member
  // refetches, and resetting the whole form on that churn wiped an elevated user's on-behalf member
  // selection mid-dialog — dropping the same-member continuous / gap warnings. Reset only when the
  // identity actually changes (a genuinely different booking context).
  useEffect(() => {
    const details = bookingResult?.details;
    if (!details) return;
    const dateKey = details.date instanceof Date ? details.date.toISOString() : String(details.date ?? "");
    const key = [dateKey, details.time, details.roomId, details.locationId, currentUser?.id ?? ""].join("|");
    if (initialisedForKeyRef.current === key) return;
    initialisedForKeyRef.current = key;
    setFormData(createInitialFormData(bookingResult, currentUser));
    bookingsLoadedRef.current = false; // Reset so we fetch bookings for the new dialog
    setServerCanBook(null); // Re-run the server pre-flight for the new slot
    setServerValidationMessage(null);
  }, [bookingResult, currentUser]);

  // Use bookings from bookingResult instead of fetching them
  // The bookings come from the calendar which already has the data loaded
  useEffect(() => {
    console.log("[MakeABookingDialog] Bookings effect triggered:", {
      formDataRoomId: formData.roomId,
      formDataDate: formData.date?.toISOString(),
      bookingsLoadedRef: bookingsLoadedRef.current,
      bookingResultExists: !!bookingResult,
      bookingResultBookingsLoaded: bookingResult?.bookingsLoaded,
      bookingResultBookingsLength: bookingResult?.bookings?.length,
    });

    if (!formData.roomId || !formData.date || bookingsLoadedRef.current) {
      console.log("[MakeABookingDialog] Early return from bookings effect");
      return;
    }

    // Check if bookings were actually loaded from the calendar context
    // If not, we need to fetch them to ensure availability checking works
    if (!bookingResult?.bookingsLoaded) {
      console.warn("[MakeABookingDialog] Bookings were not loaded - fetching from API");
      // Fetch bookings for this room and date
      const fetchBookings = async () => {
        setIsCheckingAvailability(true);
        try {
          const bookingService = new BookingService();
          const fetchedBookings = await bookingService.getBookingsForRoomByDate(formData.roomId, formData.date);
          console.log("[MakeABookingDialog] Fetched bookings from API:", {
            count: fetchedBookings.length,
            bookings: fetchedBookings.map((b) => ({
              id: b.id,
              startTime: b.startTime,
              endTime: b.endTime,
            })),
          });
          setRoomBookings(fetchedBookings);
          bookingsLoadedRef.current = true;
        } catch (err) {
          console.error("[MakeABookingDialog] Failed to fetch bookings:", err);
          // Still mark as loaded to prevent infinite loop, but bookings will be empty
          bookingsLoadedRef.current = true;
        } finally {
          setIsCheckingAvailability(false);
        }
      };
      fetchBookings();
      return;
    }

    const bookings = bookingResult?.bookings ?? [];

    console.log("[MakeABookingDialog] Using bookings from interceptor:", {
      totalBookings: bookings.length,
      roomId: formData.roomId,
    });

    // Filter to only bookings for this room that are not cancelled
    // Use occursOnDate to handle recurring bookings correctly
    const filteredBookings = bookings.filter(
      (b) => !b.isCancelled && b.roomId === formData.roomId && occursOnDate(b, formData.date)
    );

    console.log("[MakeABookingDialog] Filtered bookings for room:", {
      roomId: formData.roomId,
      count: filteredBookings.length,
      bookings: filteredBookings.map((b) => ({
        id: b.id,
        startTime: b.startTime,
        endTime: b.endTime,
        roomId: b.roomId,
      })),
    });

    setRoomBookings(filteredBookings);
    bookingsLoadedRef.current = true;
  }, [bookingResult?.bookings, bookingResult?.bookingsLoaded, formData.roomId, formData.date]);

  // Check availability whenever duration or time changes
  useEffect(() => {
    console.log("[MakeABookingDialog] Availability check triggered:", {
      bookingsLoaded: bookingsLoadedRef.current,
      roomBookingsCount: roomBookings.length,
      time: formData.time,
      durationMinutes: formData.durationMinutes,
    });

    if (!bookingsLoadedRef.current || roomBookings.length === 0) {
      // No bookings to conflict with, or bookings not loaded yet
      console.log("[MakeABookingDialog] No bookings to check against, setting available");
      setIsTimeSlotAvailable(true);
      setAvailabilityError(null);
      return;
    }

    const checkAvailability = () => {
      const endTime = calculateEndTime(formData.time, formData.durationMinutes);
      const proposedStartMinutes = timeToMinutes(formData.time);
      const proposedEndMinutes = endTimeToMinutes(endTime, formData.time);

      // R3 (hearth/docs/BOOKING_RULES.md#r3) — every booking defaults to 15-minute buffers.
      // This applies to slot bookings too (R13 mirrors the R3 default). Elevated users can
      // remove buffers retroactively via PATCH /booking/:id/buffers (R4); this dialog itself
      // doesn't expose a toggle, so creation always defaults to buffers-on.
      const hasBuffers = true;
      const proposedBufferStart = hasBuffers
        ? Math.max(0, proposedStartMinutes - BUFFER_MINUTES)
        : proposedStartMinutes;
      const proposedBufferEnd = hasBuffers
        ? Math.min(1440, proposedEndMinutes + BUFFER_MINUTES)
        : proposedEndMinutes;

      console.log("[MakeABookingDialog] Checking availability:", {
        proposedStart: formData.time,
        proposedEnd: endTime,
        proposedStartMinutes,
        proposedEndMinutes,
        proposedBufferStart,
        proposedBufferEnd,
      });

      // Sort bookings by start time
      const sortedBookings = [...roomBookings].sort(
        (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
      );

      // Task 10 — non-blocking heads-up for an elevated user's cross-member buffer_overlap, set
      // below and used as the final availabilityError instead of null IF nothing else blocked. Not
      // reset per-booking-in-loop because a later booking with no conflict would otherwise clear it.
      let elevatedBufferOverlapNotice: string | null = null;

      // Check for conflicts with each existing booking
      for (const booking of sortedBookings) {
        const bookingRange = getTimeRangeWithBuffers(
          booking.startTime,
          booking.endTime,
          booking.hasBuffers ?? true
        );

        console.log("[MakeABookingDialog] Checking against booking:", {
          bookingId: booking.id,
          bookingStart: booking.startTime,
          bookingEnd: booking.endTime,
          bookingRange,
          coreOverlap: proposedStartMinutes < bookingRange.end && proposedEndMinutes > bookingRange.start,
        });

        // Check if the proposed booking's core time overlaps with an existing booking's core time
        if (proposedStartMinutes < bookingRange.end && proposedEndMinutes > bookingRange.start) {
          // Format times for display
          const conflictStart = booking.startTime.replace(/^(\d{2})(\d{2})$/, "$1:$2");
          const conflictEnd = booking.endTime.replace(/^(\d{2})(\d{2})$/, "$1:$2");
          console.log("[MakeABookingDialog] CONFLICT FOUND - core time overlap");
          setIsTimeSlotAvailable(false);
          setAvailabilityError(
            `This duration conflicts with an existing booking (${conflictStart} - ${conflictEnd}).`
          );
          return;
        }

        // Check buffer overlap (only across parties, and only when buffers apply). R6 parity: the
        // booked-for member's own bookings AND their organisation's bookings (members + owner) are
        // ONE party, so they may butt back-to-back with no buffer between them — exactly as the
        // interceptor, the TimeAdjuster and every server path already treat them. Previously this
        // check compared member ids only, so an org owner booking under the owner account was
        // wrongly warned off butting against an org member's slot (Mindfully / Richmond White).
        const isSameMember = booking.memberId === formData.memberId;
        const isSameOrg = sameOrgMemberIds?.has(booking.memberId) ?? false;
        if (!isSameMember && !isSameOrg && proposedBufferStart < bookingRange.bufferEnd && proposedBufferEnd > bookingRange.bufferStart) {
          const conflictStart = booking.startTime.replace(/^(\d{2})(\d{2})$/, "$1:$2");
          const conflictEnd = booking.endTime.replace(/^(\d{2})(\d{2})$/, "$1:$2");
          // Task 10 — an elevated user may override a cross-member buffer_overlap, but only via the
          // REAL create attempt's 409 (backend Task 5 honours `overrideBufferConflict` there; the
          // /can-book preflight never surfaces an overridable conflict). Greying the CTA out here
          // would stop an elevated user ever reaching that 409, so don't block for them — just note
          // it and keep checking the remaining bookings. Core overlap above is unaffected.
          if (!isElevatedUser) {
            console.log("[MakeABookingDialog] CONFLICT FOUND - buffer overlap");
            setIsTimeSlotAvailable(false);
            setAvailabilityError(
              `This duration would overlap with another booking's buffer period (${conflictStart} - ${conflictEnd}).`
            );
            return;
          }
          elevatedBufferOverlapNotice = `This duration butts against another member's buffer period (${conflictStart} - ${conflictEnd}). You can still book — you'll be asked to confirm.`;
          continue;
        }
      }

      // No conflicts found (or only an elevated-overridable buffer notice, which doesn't block)
      console.log("[MakeABookingDialog] No conflicts found");
      setIsTimeSlotAvailable(true);
      setAvailabilityError(elevatedBufferOverlapNotice);
    };

    checkAvailability();
  }, [formData.time, formData.durationMinutes, formData.bookingType, formData.memberId, roomBookings, isElevatedUser, sameOrgMemberIds]);

  // Check for gap / continuous-booking warnings whenever time / duration / bookings change.
  // R10/R11 apply to slot bookings too: per R13 slots default to hasBuffers: true, so a slot
  // can leave the same unbookable gaps (or bridge a same-member continuous block) as an adhoc
  // booking. Suppressing the check for slots previously hid these warnings from elevated users,
  // who are the only role that can create slots.
  useEffect(() => {
    if (!bookingsLoadedRef.current || roomBookings.length === 0 || !formData.memberId) {
      setBookingGaps([]);
      return;
    }

    const gaps = checkBookingGaps(
      formData.time,
      formData.durationMinutes,
      roomBookings,
      formData.roomId,
      formData.date,
      formData.memberId,
      true,
      sameOrgMemberIds,
    );

    setBookingGaps(gaps);
  }, [
    formData.time,
    formData.durationMinutes,
    formData.bookingType,
    formData.memberId,
    formData.roomId,
    formData.date,
    roomBookings,
    sameOrgMemberIds,
  ]);

  // R22 — multi-room same-time warning. Scan the member's OWN bookings in OTHER rooms on this
  // date (from the calendar's day bookings) for a core-time overlap with the proposed booking.
  // This is allowed but the member is charged for both, so we surface a non-blocking warning.
  useEffect(() => {
    const allDayBookings = bookingResult?.bookings ?? [];
    if (!formData.memberId || !formData.roomId || allDayBookings.length === 0) {
      setMultiRoomConflicts([]);
      return;
    }

    const endTime = calculateEndTime(formData.time, formData.durationMinutes);
    const proposedStart = timeToMinutes(formData.time);
    const proposedEnd = endTimeToMinutes(endTime, formData.time);

    const conflicts = allDayBookings
      .filter(
        (b) =>
          !b.isCancelled &&
          b.memberId === formData.memberId &&
          b.roomId !== formData.roomId &&
          occursOnDate(b, formData.date) &&
          // Core-time overlap (R7's "never overlap" is per-room; cross-room overlap is allowed → warn)
          proposedStart < endTimeToMinutes(b.endTime, b.startTime) &&
          proposedEnd > timeToMinutes(b.startTime),
      )
      .map((b) => ({ startTime: b.startTime, endTime: b.endTime }));

    setMultiRoomConflicts(conflicts);
  }, [formData.time, formData.durationMinutes, formData.memberId, formData.roomId, formData.date, bookingResult]);

  // Server /can-book pre-flight (debounced). Runs whenever the proposed booking changes and lets
  // the server have the final say on whether the slot is bookable, surfacing its reason. Stale
  // responses are ignored via a sequence guard; on error we leave serverCanBook null (no block).
  useEffect(() => {
    // A declined interceptor verdict already blocks the whole form — no need to ask the server.
    if (isDeclined) {
      setServerCanBook(null);
      setServerValidationMessage(null);
      setServerWarnings([]);
      setRecurringConflictDates([]);
      return;
    }
    if (!formData.roomId || !formData.memberId || !formData.locationId || !formData.date || !formData.time) {
      return;
    }

    const seq = ++canBookSeqRef.current;
    setIsCheckingCanBook(true);

    const timer = setTimeout(async () => {
      try {
        const bookingService = new BookingService();
        const endTime = calculateEndTime(formData.time, formData.durationMinutes);
        // Carry the cadence so the conflict preview honours fortnightly/monthly/quarterly instead of
        // defaulting to weekly interval 1. The only cadences the picker offers are weekly/monthly;
        // narrow away the (unused) "daily" so the request's "weekly" | "monthly" type is satisfied.
        const patternFrequency = formData.recurrencePattern?.frequency;
        const recurrenceFrequency = patternFrequency === "monthly" ? "monthly" : patternFrequency === "weekly" ? "weekly" : undefined;
        const response = await bookingService.checkCanBook({
          locationId: formData.locationId,
          roomId: formData.roomId,
          bookingForMemberId: formData.memberId,
          date: formatIsoDate(formData.date),
          startTime: formData.time,
          endTime,
          type: formData.bookingType,
          isRecurring: formData.isRecurring || formData.bookingType === "slot",
          recurrenceCount: formData.recurrencePattern?.occurrencesToCheck,
          recurrenceFrequency,
          recurrenceInterval: formData.recurrencePattern?.interval,
          amenityIds: [],
          hasBuffers: true,
        });
        if (seq !== canBookSeqRef.current) return; // a newer request superseded this one
        setServerCanBook(response.canBook);
        setServerValidationMessage(extractCanBookMessage(response));
        setServerWarnings(response.warnings ?? []);
        setRecurringConflictDates(
          response.canBook ? [] : response.validations.recurringConflicts.conflictingDates ?? [],
        );
      } catch {
        if (seq !== canBookSeqRef.current) return;
        // Don't block on a failed pre-flight — POST /bookings re-validates server-side.
        setServerCanBook(null);
        setServerValidationMessage(null);
        setServerWarnings([]);
        setRecurringConflictDates([]);
      } finally {
        if (seq === canBookSeqRef.current) setIsCheckingCanBook(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    isDeclined,
    formData.roomId,
    formData.memberId,
    formData.locationId,
    formData.date,
    formData.time,
    formData.durationMinutes,
    formData.bookingType,
    formData.isRecurring,
    formData.recurrencePattern,
  ]);

  // Navigation functions
  const navigateTo = useCallback((screen: ScreenId) => {
    setScreenHistory((prev) => [...prev, screen]);
    setCurrentScreen(screen);
  }, []);

  const goBack = useCallback(() => {
    setScreenHistory((prev) => {
      if (prev.length <= 1) return prev;
      const newHistory = prev.slice(0, -1);
      setCurrentScreen(newHistory[newHistory.length - 1]);
      return newHistory;
    });
  }, []);

  // Form data update
  const updateFormData = useCallback((partial: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  }, []);

  // Reset flow
  const resetFlow = useCallback(() => {
    setCurrentScreen("book");
    setScreenHistory(["book"]);
    setFormData(createInitialFormData(bookingResult, currentUser));
    setIsSubmitting(false);
    setError(null);
    setCreatedBooking(null);
    setRoomBookings([]);
    setIsCheckingAvailability(false);
    setIsTimeSlotAvailable(true);
    setAvailabilityError(null);
    setBookingGaps([]);
    setBufferOverrideOpen(false);
    setBufferOverrideMessage(null);
    bookingsLoadedRef.current = false;
  }, [bookingResult, currentUser]);

  // Submit booking. `overrideBufferConflict` is only ever true on the resubmit triggered by
  // confirmBufferOverride below — normal callers (the CTAs in BookScreen/RepeatSetupScreen) call
  // submitBooking() with no argument.
  const submitBooking = useCallback(async (overrideBufferConflict: boolean = false) => {
    // Defense in depth: never submit a declined interceptor verdict, even if a CTA somehow fired.
    if (bookingResult?.status === "declined") {
      setError(bookingResult?.message ?? "This booking can't be made.");
      return;
    }
    // …nor a slot the server pre-flight has explicitly blocked.
    if (serverCanBook === false) {
      setError(serverValidationMessage ?? "This time isn't available — please pick another slot.");
      return;
    }
    // A booking note is required when an elevated user books for themselves OR when waiving billing
    // (a FOC booking must record why). Block submit until one is entered — a backstop for the disabled
    // CTA that also covers the RepeatSetupScreen path which calls submitBooking directly.
    const waiveRequiresNote = isElevatedUser && !!formData.waiveBilling;
    if ((requiresStaffNote || waiveRequiresNote) && !formData.staffNote.trim()) {
      setError(
        waiveRequiresNote && !requiresStaffNote
          ? "Please add a booking note — it's required when you waive billing for a booking."
          : "Please add a booking note — it's required when booking for yourself.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const bookingService = new BookingService();
      const endTime = calculateEndTime(formData.time, formData.durationMinutes);

      const params: CreateBookingParams = {
        locationId: formData.locationId,
        roomId: formData.roomId,
        memberId: formData.memberId,
        date: formData.date,
        startTime: formData.time,
        endTime: endTime,
        type: formData.bookingType,
        // R3 — all bookings default to 15-min buffers, slot included (R13). PATCH endpoint is
        // the R4 escape hatch for elevated users post-creation.
        hasBuffers: true,
        isRecurring: formData.isRecurring || formData.bookingType === "slot",
        // TWO different things, deliberately sent separately:
        //   recurrenceCount       — how many occurrences the server should CHECK for conflicts.
        //   recurrenceOccurrences — the operator's chosen series length ("repeat 2 times").
        // They were previously the same value, and the server (correctly) refuses to store the
        // check window as a real end — which is why choosing "2 times" still produced a series that
        // repeated forever on the calendar.
        recurrenceCount: formData.recurrencePattern?.occurrencesToCheck,
        recurrenceOccurrences: formData.recurrencePattern?.occurrencesToCheck,
        recurrenceFrequency: formData.recurrencePattern?.frequency as "weekly" | "monthly" | undefined,
        recurrenceInterval: formData.recurrencePattern?.interval,
        wasWarnedAboutUnbookableGap: bookingGaps.length > 0,
        isGroupBooking: formData.isGroupBooking,
        // R24 pull-forward — only for slot bookings, when the admin chose an earlier start.
        slotStartOverride:
          formData.bookingType === "slot" && formData.slotStartOverride
            ? formatIsoDate(formData.slotStartOverride)
            : undefined,
        // Elevated past-date waive-billing (Task 7). Only sent when elevated and relevant; false otherwise.
        waiveBilling: isElevatedUser ? (formData.waiveBilling ?? false) : false,
        // Elevated cross-member buffer override (Task 10) — identical payload to the first attempt,
        // plus this flag, sent only on the confirmBufferOverride resubmit.
        overrideBufferConflict,
      };

      const booking = await bookingService.createNewBooking(params);

      // Persist the booking note as a booking comment (authored by the creator) whenever one was
      // entered — mandatory for on-behalf bookings, optional for member/admin-self bookings. Non-fatal:
      // the booking already exists, so a note failure is logged rather than rolled back. The author is
      // set server-side from auth, which is what lets a member see only their OWN note on their view.
      if (formData.staffNote.trim()) {
        try {
          const commentService = new CommentService();
          await commentService.createComment({
            entityType: "booking",
            entityId: booking.id,
            content: formData.staffNote.trim(),
          });
        } catch (noteError) {
          // Non-fatal by design — the booking already exists, so a note failure must not roll it
          // back. But it was previously silent, which is exactly why a member's note going missing
          // went unnoticed: every member note 403'd and vanished without a trace. Tell the user so
          // they can re-add it rather than discovering it absent later.
          console.error("Failed to save booking note comment:", noteError);
          toast.error("Your booking was created, but the note could not be saved. Please add it from the booking.");
        }
      }

      // Clear cache and notify
      bookingService.api.clearCache();

      setCreatedBooking(booking);
      onBookingCreated?.(booking);
      navigateTo("confirmation");
    } catch (err) {
      // Task 10 — the REAL create attempt is the only place an overridable buffer_overlap is known;
      // the /can-book preflight above never surfaces one. Match the shape Task 5's route returns on
      // 409 (`{ error, conflictType: "buffer_overlap", details }`) — remoteBookingApi surfaces it as
      // `(error as any).conflictType`. Only elevated users get the override dialog, and only on the
      // FIRST attempt — if the override resubmit itself 409s again, fall through to the hard error
      // like any other failure.
      const conflictType = (err as { conflictType?: string } | null | undefined)?.conflictType;
      if (!overrideBufferConflict && isElevatedUser && conflictType === "buffer_overlap") {
        setBufferOverrideMessage(
          err instanceof Error ? err.message : "This time butts against another member's buffer.",
        );
        setBufferOverrideOpen(true);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred while creating the booking");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, bookingGaps, onBookingCreated, navigateTo, bookingResult, serverCanBook, serverValidationMessage, isElevatedUser, requiresStaffNote]);

  // Task 10 — "Book anyway" on the BufferOverrideDialog. Resubmits the IDENTICAL payload (formData
  // hasn't changed) plus overrideBufferConflict: true. Closes the dialog once the retry settles,
  // whether it succeeded (submitBooking already navigated to "confirmation") or failed for some
  // other reason (submitBooking already set `error`, which the underlying screen now shows).
  const confirmBufferOverride = useCallback(async () => {
    await submitBooking(true);
    setBufferOverrideOpen(false);
  }, [submitBooking]);

  const contextValue: MakeABookingDialogState = useMemo(
    () => ({
      currentScreen,
      screenHistory,
      navigateTo,
      goBack,
      formData,
      updateFormData,
      isSubmitting,
      error,
      createdBooking,
      // Combine the local fast-check and the async server pre-flight: show "checking" while either
      // runs, block when the local check OR the server says no, and prefer the local message,
      // falling back to the server's reason.
      isCheckingAvailability: isCheckingAvailability || isCheckingCanBook,
      isTimeSlotAvailable: isTimeSlotAvailable && serverCanBook !== false,
      availabilityError: availabilityError ?? serverValidationMessage,
      recurringConflictDates,
      bookingGaps,
      serverWarnings,
      multiRoomConflicts,
      isDeclined,
      declineMessage,
      declineReason,
      isPastDateWarning,
      pastOccurrenceCount,
      currentUser,
      isElevatedUser,
      requiresStaffNote,
      submitBooking,
      resetFlow,
      onClose,
      bufferOverrideOpen,
      bufferOverrideMessage,
      confirmBufferOverride,
      setBufferOverrideOpen,
    }),
    [
      currentScreen,
      screenHistory,
      navigateTo,
      goBack,
      formData,
      updateFormData,
      isSubmitting,
      error,
      createdBooking,
      isCheckingAvailability,
      isCheckingCanBook,
      isTimeSlotAvailable,
      serverCanBook,
      availabilityError,
      serverValidationMessage,
      recurringConflictDates,
      bookingGaps,
      serverWarnings,
      multiRoomConflicts,
      isDeclined,
      declineMessage,
      declineReason,
      isPastDateWarning,
      pastOccurrenceCount,
      currentUser,
      isElevatedUser,
      requiresStaffNote,
      submitBooking,
      resetFlow,
      onClose,
      bufferOverrideOpen,
      bufferOverrideMessage,
      confirmBufferOverride,
    ],
  );

  return (
    <MakeABookingDialogContext.Provider value={contextValue}>
      {children}
    </MakeABookingDialogContext.Provider>
  );
};

export default MakeABookingDialogProvider;
